import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAdminOffer } from "@/hooks/api/use-admin";
import { adminService } from "@/api/services/admin.service";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle, 
  XCircle, 
  DollarSign, 
  Users, 
  FileText, 
  Phone, 
  Mail, 
  MapPin,
  Calendar,
  Download,
  Eye
} from "lucide-react";
import apiClient from "@/api/client";

const AdminOfferDetails = () => {
  const { t, isRTL, language } = useLanguage();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const ArrowIcon = isRTL ? ArrowRight : ArrowLeft;
  const [activeTab, setActiveTab] = useState("offer");
  
  const { data, isLoading, error, refetch } = useAdminOffer(Number(id), { enabled: !!id });
  
  const offer = data?.data;

  // Mutations for offer actions
  const acceptOfferMutation = useMutation({
    mutationFn: adminService.acceptOffer,
    onSuccess: () => {
      toast({
        title: t('admin.offers.acceptSuccess') || 'Offer accepted successfully',
        description: t('admin.offers.acceptSuccessDesc') || 'The offer has been accepted and the client will be notified.',
      });
      refetch();
      queryClient.invalidateQueries({ queryKey: ['adminOffers'] });
    },
    onError: (error: any) => {
      toast({
        title: t('admin.offers.acceptError') || 'Failed to accept offer',
        description: error.message || t('admin.offers.acceptErrorDesc') || 'An error occurred while accepting the offer.',
        variant: 'destructive',
      });
    },
  });

  const rejectOfferMutation = useMutation({
    mutationFn: adminService.rejectOffer,
    onSuccess: () => {
      toast({
        title: t('admin.offers.rejectSuccess') || 'Offer rejected successfully',
        description: t('admin.offers.rejectSuccessDesc') || 'The offer has been rejected and the client will be notified.',
      });
      refetch();
      queryClient.invalidateQueries({ queryKey: ['adminOffers'] });
    },
    onError: (error: any) => {
      toast({
        title: t('admin.offers.rejectError') || 'Failed to reject offer',
        description: error.message || t('admin.offers.rejectErrorDesc') || 'An error occurred while rejecting the offer.',
        variant: 'destructive',
      });
    },
  });

  const handleAcceptOffer = () => {
    if (offer?.id) {
      acceptOfferMutation.mutate(offer.id);
    }
  };

  const handleRejectOffer = () => {
    if (offer?.id) {
      rejectOfferMutation.mutate(offer.id);
    }
  };

  const handleDownloadAttachment = async (attachment: any) => {
    if (!offer?.id) return;
    
    try {
      const response = await apiClient.get(
        `/api/v1/admin/offers/${offer.id}/attachments/${attachment.id}/download`,
        {
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.original_name || attachment.file_name || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
        <div className="flex items-center gap-3 sm:gap-4">
          <Skeleton className="h-9 w-9 sm:h-10 sm:w-10" />
          <Skeleton className="h-6 sm:h-8 w-32 sm:w-48" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <Skeleton className="h-5 sm:h-6 w-28 sm:w-32" />
              </CardHeader>
              <CardContent className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                <Skeleton className="h-3 sm:h-4 w-full" />
                <Skeleton className="h-3 sm:h-4 w-3/4" />
                <Skeleton className="h-3 sm:h-4 w-1/2" />
              </CardContent>
            </Card>
          </div>
          <div className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <Skeleton className="h-5 sm:h-6 w-20 sm:w-24" />
              </CardHeader>
              <CardContent className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                <Skeleton className="h-3 sm:h-4 w-full" />
                <Skeleton className="h-3 sm:h-4 w-3/4" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="space-y-4 sm:space-y-6 p-4 sm:p-6" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <Link to="/admin/dashboard/offers">
            <Button variant="outline" size="sm" className="w-full sm:w-auto">
              <ArrowIcon className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
              {t('common.back') || 'Back'}
            </Button>
          </Link>
          <h1 className="text-xl sm:text-2xl font-bold">
            {t('admin.offers.offerNotFound') || 'Offer Not Found'}
          </h1>
        </div>
        <Card>
          <CardContent className="py-6 sm:py-8 px-4 sm:px-6">
            <div className="text-center text-muted-foreground text-sm sm:text-base">
              {t('admin.offers.offerNotFoundDesc') || 'The offer you are looking for does not exist or has been deleted.'}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'accepted':
        return t('admin.offers.accepted') || 'مقبول';
      case 'rejected':
        return t('admin.offers.rejected') || 'مرفوض';
      case 'pending':
        return t('admin.offers.pending') || 'معلق';
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col gap-4">
            {/* Top row: Back button */}
            <div className="flex items-center justify-between">
              <Link to="/admin/dashboard/offers" className="group">
                <Button variant="outline" size="sm" className="group-hover:bg-gray-50 dark:group-hover:bg-gray-700 transition-colors text-xs sm:text-sm">
                  <ArrowIcon className={`${isRTL ? 'ml-2' : 'mr-2'} h-3.5 w-3.5 sm:h-4 sm:w-4`} />
                  {t('common.back') || 'Back'}
                </Button>
              </Link>
              
              {/* Action Buttons - Desktop */}
              <div className="hidden md:flex items-center gap-2">
                {offer.status === 'pending' && (
                  <>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm">
                          <CheckCircle className={`${isRTL ? 'ml-2' : 'mr-2'} h-3.5 w-3.5 sm:h-4 sm:w-4`} />
                          {t('admin.offers.accept') || 'Accept'}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {t('admin.offers.acceptConfirm') || 'Accept Offer'}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('admin.offers.acceptConfirmDesc') || 'Are you sure you want to accept this offer? The client will be notified and this action cannot be undone.'}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>
                            {t('common.cancel') || 'Cancel'}
                          </AlertDialogCancel>
                          <AlertDialogAction onClick={handleAcceptOffer}>
                            {t('admin.offers.accept') || 'Accept'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm" className="text-xs sm:text-sm">
                          <XCircle className={`${isRTL ? 'ml-2' : 'mr-2'} h-3.5 w-3.5 sm:h-4 sm:w-4`} />
                          {t('admin.offers.reject') || 'Reject'}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {t('admin.offers.rejectConfirm') || 'Reject Offer'}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('admin.offers.rejectConfirmDesc') || 'Are you sure you want to reject this offer? The client will be notified and this action cannot be undone.'}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>
                            {t('common.cancel') || 'Cancel'}
                          </AlertDialogCancel>
                          <AlertDialogAction onClick={handleRejectOffer}>
                            {t('admin.offers.reject') || 'Reject'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            </div>
            
            {/* Title row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {t('admin.offers.offerDetails') || 'Offer Details'}
                </h1>
                <Badge className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium ${
                  offer?.status === 'accepted' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' 
                    : offer?.status === 'rejected'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                }`}>
                  {getStatusText(offer?.status)}
                </Badge>
              </div>
              
              {/* Action Buttons - Mobile */}
              <div className="flex md:hidden items-center gap-2">
                {offer.status === 'pending' && (
                  <>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs">
                          <CheckCircle className={`${isRTL ? 'ml-1.5' : 'mr-1.5'} h-3.5 w-3.5`} />
                          {t('admin.offers.accept') || 'Accept'}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {t('admin.offers.acceptConfirm') || 'Accept Offer'}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('admin.offers.acceptConfirmDesc') || 'Are you sure you want to accept this offer?'}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                          <AlertDialogCancel className="w-full sm:w-auto">
                            {t('common.cancel') || 'Cancel'}
                          </AlertDialogCancel>
                          <AlertDialogAction onClick={handleAcceptOffer} className="w-full sm:w-auto">
                            {t('admin.offers.accept') || 'Accept'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm" className="flex-1 text-xs">
                          <XCircle className={`${isRTL ? 'ml-1.5' : 'mr-1.5'} h-3.5 w-3.5`} />
                          {t('admin.offers.reject') || 'Reject'}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {t('admin.offers.rejectConfirm') || 'Reject Offer'}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('admin.offers.rejectConfirmDesc') || 'Are you sure you want to reject this offer?'}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                          <AlertDialogCancel className="w-full sm:w-auto">
                            {t('common.cancel') || 'Cancel'}
                          </AlertDialogCancel>
                          <AlertDialogAction onClick={handleRejectOffer} className="w-full sm:w-auto">
                            {t('admin.offers.reject') || 'Reject'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          <TabsList className="grid w-full grid-cols-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-1">
            <TabsTrigger value="offer" className={`flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-white ${isRTL ? 'flex-row-reverse' : ''}`}>
              <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('admin.offers.offerInfo') || 'Offer Information'}</span>
              <span className="sm:hidden">{isRTL ? 'العرض' : 'Offer'}</span>
            </TabsTrigger>
            <TabsTrigger value="client" className={`flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-white ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('admin.offers.clientInfo') || 'Client Information'}</span>
              <span className="sm:hidden">{isRTL ? 'العميل' : 'Client'}</span>
            </TabsTrigger>
          </TabsList>

          {/* Offer Information Tab */}
          <TabsContent value="offer" className="space-y-4 sm:space-y-6">
            <Tabs defaultValue="tender" className="space-y-4 sm:space-y-6">
              <TabsList className="grid w-full grid-cols-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-1">
                <TabsTrigger value="tender" className={`flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs data-[state=active]:bg-primary data-[state=active]:text-white ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="truncate">{isRTL ? 'المناقصة' : 'Tender'}</span>
                </TabsTrigger>
                <TabsTrigger value="details" className={`flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs data-[state=active]:bg-primary data-[state=active]:text-white ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="truncate">{isRTL ? 'التفاصيل' : 'Details'}</span>
                </TabsTrigger>
                <TabsTrigger value="attachments" className={`flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs data-[state=active]:bg-primary data-[state=active]:text-white ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Download className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="truncate">{isRTL ? 'المرفقات' : 'Files'}</span>
                </TabsTrigger>
              </TabsList>

              {/* Tender Sub-tab */}
              <TabsContent value="tender" className="space-y-3 sm:space-y-4">
                <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="flex items-center text-base sm:text-lg">
                      <FileText className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 sm:h-5 sm:w-5`} />
                      {t('admin.offers.relatedTender') || 'Related Tender'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm sm:text-base">
                        {(offer.tender as any)?.title?.[language] || (offer.tender as any)?.title?.en || 'N/A'}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {(offer.tender as any)?.description?.[language] || (offer.tender as any)?.description?.en || 'N/A'}
                      </p>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-3 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          {offer.tender?.budget_min && offer.tender?.budget_max
                            ? `${offer.tender.budget_min} - ${offer.tender.budget_max}`
                            : 'N/A'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          {offer.tender?.deadline ? new Date(offer.tender.deadline).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Offer Details Sub-tab */}
              <TabsContent value="details" className="space-y-3 sm:space-y-4">
                <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="flex items-center text-base sm:text-lg">
                      <DollarSign className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 sm:h-5 sm:w-5`} />
                      {t('admin.offers.offerDetails') || 'Offer Details'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 p-3 sm:p-4 bg-gradient-to-r from-green-50 to-green-100/50 dark:from-green-950/50 dark:to-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
                      <label className="text-xs sm:text-sm font-medium text-green-700 dark:text-green-400">
                        {t('admin.offers.proposedAmount') || 'Proposed Amount'}
                      </label>
                      <p className="text-xl sm:text-2xl font-bold text-green-700 dark:text-green-300">
                        {isRTL ? 'ر.س ' : ''}{parseFloat(String(offer.proposed_amount || 0)).toLocaleString()}{isRTL ? '' : ' SAR'}
                      </p>
                    </div>
                    {offer.notes && (
                      <div className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <label className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                          {t('admin.offers.notes') || 'Notes'}
                        </label>
                        <p className="mt-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">{offer.notes}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <label className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                          {t('admin.offers.submittedAt') || 'Submitted At'}
                        </label>
                        <p className="flex items-center gap-1 text-gray-900 dark:text-gray-100">
                          <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          <span className="text-xs sm:text-sm">{new Date(offer.submitted_at || offer.created_at).toLocaleDateString()}</span>
                        </p>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <label className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                          {t('admin.offers.offerId') || 'Offer ID'}
                        </label>
                        <p className="font-mono text-gray-900 dark:text-gray-100 text-xs sm:text-sm">#{offer.id}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Attachments Sub-tab */}
              <TabsContent value="attachments" className="space-y-3 sm:space-y-4">
                <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="flex items-center text-base sm:text-lg">
                      <Download className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 sm:h-5 sm:w-5`} />
                      {t('admin.offers.attachments') || 'Attachments'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6 space-y-2 sm:space-y-3">
                    {offer.attachments && offer.attachments.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                        {offer.attachments.map((attachment: any) => (
                          <div key={attachment.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex-shrink-0">
                                <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{attachment.original_name || attachment.file_name}</p>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-[10px] sm:text-xs px-1.5 py-0.5 h-5">
                                    {attachment.document_type === 'financial' ? t('offers.documentTypeFinancial') || 'مالي' : t('offers.documentTypeTechnical') || 'فني'}
                                  </Badge>
                                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
                                    {attachment.formatted_size || `${(attachment.file_size / 1024 / 1024).toFixed(2)} MB`}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadAttachment(attachment)}
                              className="w-full sm:w-auto text-xs sm:text-sm h-8 sm:h-9"
                            >
                              <Download className={`${isRTL ? 'ml-1.5' : 'mr-1.5'} h-3.5 w-3.5 sm:h-4 sm:w-4`} />
                              {t('common.download') || 'تحميل'}
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 sm:py-12">
                        <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                          <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                          {t('admin.offers.noAttachments') || 'No attachments'}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Client Information Tab */}
          <TabsContent value="client" className="space-y-4 sm:space-y-6">
            <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center text-base sm:text-lg">
                  <Users className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 sm:h-5 sm:w-5`} />
                  {t('admin.offers.clientInfo') || 'Client Information'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                <div className="flex items-center justify-center mb-4 sm:mb-6">
                  <div className="text-center">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-700 shadow-lg">
                      <Users className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                    </div>
                    <h4 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mt-3">
                      {offer.client?.name || 'N/A'}
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 text-center">
                      {offer.client?.company_name || 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                  <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50">
                      <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="min-w-0">
                      <label className="text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400">
                        {t('admin.offers.email') || 'Email'}
                      </label>
                      <p className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 font-medium truncate">{(offer.client as any)?.email || 'N/A'}</p>
                    </div>
                  </div>
                  {(offer.client as any)?.phone && (
                    <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/50">
                        <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="min-w-0">
                        <label className="text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400">
                          {t('admin.offers.phone') || 'Phone'}
                        </label>
                        <p className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 font-medium truncate">{(offer.client as any)?.phone}</p>
                      </div>
                    </div>
                  )}
                  {(offer.client as any)?.address && (
                    <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg sm:col-span-2">
                      <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/50">
                        <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div className="min-w-0">
                        <label className="text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400">
                          {t('admin.offers.address') || 'Address'}
                        </label>
                        <p className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 font-medium">{(offer.client as any)?.address}</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between text-xs sm:text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      {t('admin.offers.clientStatus') || 'Client Status'}
                    </span>
                    <Badge className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium ${
                      (offer.client as any)?.status === 'approved' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' 
                        : (offer.client as any)?.status === 'rejected'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                    }`}>
                      {(offer.client as any)?.status || 'N/A'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">
                  {t('admin.offers.quickActions') || 'Quick Actions'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 space-y-2 sm:space-y-3">
                <Link to={`/admin/dashboard/tenders/${offer.tender?.id}`}>
                  <Button variant="outline" className="w-full justify-start text-xs sm:text-sm h-9 sm:h-10">
                    <Eye className={`${isRTL ? 'ml-2' : 'mr-2'} h-3.5 w-3.5 sm:h-4 sm:w-4`} />
                    {t('admin.offers.viewTender') || 'View Tender'}
                  </Button>
                </Link>
                
                <Link to={`/admin/dashboard/clients/${offer.client?.id}`}>
                  <Button variant="outline" className="w-full justify-start text-xs sm:text-sm h-9 sm:h-10">
                    <Users className={`${isRTL ? 'ml-2' : 'mr-2'} h-3.5 w-3.5 sm:h-4 sm:w-4`} />
                    {t('admin.offers.viewClient') || 'View Client'}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminOfferDetails;
